// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { attributesCalculation } from "@store/Framework/Common/Utilities/attributesCalculation";
import {
    attributeTypeLabelTextStyles,
    attributeTypeValuesTextStyles,
    attributeValuesTextStyles,
    specificationWrapperStyles,
    specStyles,
    wrapperStyles,
} from "@store/Themes/common/mixins/attributes";

const enum fields {
    maximumAttributes = "maximumAttributes",
}
/* Store Custom Code - End */

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    // wrapper?: InjectableCss; // Store Custom Code
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // Starter Custom Code
    list?: TypographyPresentationProps;
    item?: TypographyPresentationProps;
    noResult?: TypographyPresentationProps;
    // Store Custom Code
    wrapper?: GridItemProps;
    spec?: TypographyPresentationProps;
    attributeValuesText?: TypographyPresentationProps;
    specificationWrapper?: GridContainerProps;
    buttonGrid?: GridContainerProps;
    buttonGridItem?: GridItemProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    // Store Custom Code
    wrapper: {
        width: [12, 12, 12, 3, 3],
        css: css`
            ${wrapperStyles()};
        `,
    },
    attributeTypeValuesText: {
        css: css`
            ${attributeTypeValuesTextStyles()};
        `,
    },
    attributeValuesText: {
        css: css`
            ${attributeValuesTextStyles()};
        `,
    },
    spec: {
        css: css`
            ${specStyles("", 0)};
        `,
    },
    specificationWrapper: {
        css: css`
            ${specificationWrapperStyles()};
        `,
        offsetCss: css`
            margin: unset;
        `,
    },
    attributeTypeLabelText: {
        css: css`
            ${attributeTypeLabelTextStyles()};
        `,
    },
    buttonGrid: {
        offsetCss: css`
            margin: unset;
        `,
    },
    buttonGridItem: {
        width: [5, 4, 4, 4, 2],
        css: css`
            padding: ${pxToRem(5)};
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({
    product: { brand, attributeTypes },
    // Store Custom Code
    product,
    fields: { maximumAttributes },
}) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        /* Starter Custom Code - Start */
        return <Typography {...styles.noResult}>{translate("There are no specifications available.")}</Typography>;
        /* Starter Custom Code - End */
    }

    /* Store Custom Code - Start */
    const limitedAttributeTypes = (product.attributeTypes ?? []).slice(0, maximumAttributes);

    const attributesArray = attributesCalculation(limitedAttributeTypes);

    return (
        <>
            <Typography as="p" {...styles.spec}>
                {translate("Specifications")}
            </Typography>
            <GridContainer {...styles.specificationWrapper}>
                {attributesArray &&
                    attributesArray.map(attributeArray => (
                        <GridItem
                            {...styles.wrapper}
                            data-test-selector="productDetails_attributes"
                            key={attributeArray.length.toString()}
                        >
                            {attributeArray.map(
                                (attributeType: {
                                    id: { toString: () => string | number | undefined };
                                    label: any;
                                    name: any;
                                    attributeValues: any;
                                }) => (
                                    <Typography
                                        as="p"
                                        key={attributeType.id.toString()}
                                        data-test-selector="attributes_item"
                                        data-attributetypeid={attributeType.id}
                                        {...styles.attributeTypeValuesText}
                                    >
                                        <Typography
                                            data-test-selector="attributes_item_label"
                                            {...styles.attributeTypeLabelText}
                                        >
                                            {attributeType.label || attributeType.name}:
                                        </Typography>

                                        <Typography
                                            {...styles.attributeValuesText}
                                            data-test-selector="attributes_item_value"
                                        >
                                            {(attributeType.attributeValues ?? [])
                                                .map(
                                                    (attributeValue: { valueDisplay: any }) =>
                                                        attributeValue.valueDisplay,
                                                )
                                                .join(", ")}
                                        </Typography>
                                    </Typography>
                                ),
                            )}
                        </GridItem>
                    ))}{" "}
            </GridContainer>
        </>
    );
    /* Store Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        // Store Custom Code
        fieldDefinitions: [
            {
                name: fields.maximumAttributes,
                displayName: "Maximum attributes to display",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                tooltip: "integer value",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
